import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  useNavigate,
  useParams
} from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  MapPin
} from "lucide-react";

import {
  getAllExamRooms,
  getSeatingPlanBySchedule,
  createSeatingPlan,
  updateSeatingPlan,
  updateSeatingPlanStatus,
  getStudentsForSeating
} from "../../api/examsschedule/examSeatingApi";
import {
  getAllExamRoomsPortal,
  getSeatingPlanBySchedulePortal,
  createSeatingPlanPortal,
  updateSeatingPlanPortal,
  updateSeatingPlanStatusPortal,
  getStudentsForSeatingPortal
} from "../../api/examsschedule/examSeatingPortalApi";
import {
  getAllExamSchedules
} from "../../api/examsschedule/examApi";
import {
  getAllExamSchedulesPortal
} from "../../api/examsschedule/examPortalApi";
import {
  useStaffAuth
} from "../../context/StaffAuthContext";
import SeatingRoomCard from "../../components/examsschedule/SeatingRoomCard";
import {
  pickList,
  toId,
  formatDate
} from "../../utils/examsschedule/examHelpers";
import {
  Button,
  Select,
  Textarea,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  Skeleton
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

const ExamSeatingPage = ({ isPortal = false }) => {
  const { scheduleId } = useParams();
  const navigate = useNavigate();
  const staffAuth = useStaffAuth();
  const canManage = isPortal
    ? staffAuth?.hasPermission?.("EXAM_MANAGEMENT_FULL")
    : true;

  const [schedule, setSchedule] = useState(null);
  const [plan, setPlan] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [students, setStudents] = useState([]);
  const [sections, setSections] = useState([]);
  const [sectionFilter, setSectionFilter] = useState("");
  const [allocations, setAllocations] = useState([]);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const toast = useToast();

  const examsPath = isPortal ? "/staff-panel/exam/exams" : "/exam/exams";
  const schedulesPath = isPortal
    ? "/staff-panel/exam/schedules"
    : "/exam/schedules";
  const locked = plan?.status === "FINAL";

  const loadStudents = useCallback(
    async (secId) => {
      const filters = secId ? { sectionId: secId } : {};
      const stuRes = isPortal
        ? await getStudentsForSeatingPortal(scheduleId, filters)
        : await getStudentsForSeating(scheduleId, filters);
      setStudents(pickList(stuRes, ["students", "data"]));
      const secList = pickList(stuRes, ["sections"]);
      if (secList.length) setSections(secList);
    },
    [scheduleId, isPortal]
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);

      const schRes = isPortal
        ? await getAllExamSchedulesPortal({})
        : await getAllExamSchedules({});
      const allSch = pickList(schRes, ["schedules", "data"]);
      const sch =
        allSch.find((s) => toId(s) === scheduleId) ||
        allSch.find((s) => toId(s._id) === scheduleId);
      setSchedule(sch || null);

      const roomRes = isPortal
        ? await getAllExamRoomsPortal({ isActive: "true" })
        : await getAllExamRooms({ isActive: "true" });
      setRooms(pickList(roomRes, ["rooms", "data"]));

      await loadStudents("");

      try {
        const planRes = isPortal
          ? await getSeatingPlanBySchedulePortal(scheduleId)
          : await getSeatingPlanBySchedule(scheduleId);
        const p = planRes.seatingPlan || planRes.data || planRes;
        setPlan(p && p._id ? p : null);
        setNotes(p?.notes || "");
        setAllocations(
          (p?.rooms || []).map((r) => {
            const seatAllocations = (r.seatAllocations || []).map((s) => ({
              benchNo: Number(s.benchNo),
              seatIndex: Number(s.seatIndex) || 1,
              studentId: toId(s.studentId),
              classId: toId(s.classId) || toId(sch?.classId) || "",
              sectionId: toId(s.sectionId),
              seatNumber: s.seatNumber || "",
            }));

            let seats = seatAllocations;
            if (seats.length === 0 && (r.studentIds || []).length > 0) {
              const per = Number(r.seatsPerBench) || 1;
              seats = (r.studentIds || []).map((sid, i) => {
                const benchNo = Math.floor(i / per) + 1;
                const seatIndex = (i % per) + 1;
                return {
                  benchNo,
                  seatIndex,
                  studentId: toId(sid),
                  classId: toId(sch?.classId) || "",
                  sectionId: "",
                  seatNumber: `${benchNo}${per === 2 ? (seatIndex === 1 ? "A" : "B") : ""}`,
                };
              });
            }
            return {
              roomId: toId(r.roomId) || toId(r),
              invigilatorIds: (r.invigilatorIds || []).map(toId),
              seatAllocations: seats,
              startTime: r.startTime || "",
              endTime: r.endTime || "",
              totalBenches: r.totalBenches,
              seatsPerBench: r.seatsPerBench,
              capacity: r.capacity,
            };
          })
        );
      } catch (e) {
        if (e.response?.status === 404) {
          setPlan(null);
          setAllocations([]);
          setNotes("");
        } else {
          throw e;
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load seating data");
    } finally {
      setLoading(false);
    }
  }, [scheduleId, isPortal, loadStudents]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSectionFilter = async (secId) => {
    setSectionFilter(secId);
    try {
      await loadStudents(secId);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to filter students");
    }
  };

  const seatedInOtherRooms = useMemo(() => {
    const byRoom = allocations.map((a) =>
      new Set((a.seatAllocations || []).map((s) => toId(s.studentId)))
    );
    return byRoom;
  }, [allocations]);

  const studentsForRoom = (roomIndex) => {
    const takenElsewhere = new Set();
    seatedInOtherRooms.forEach((set, i) => {
      if (i === roomIndex) return;
      set.forEach((id) => takenElsewhere.add(id));
    });
    return students.filter((s) => !takenElsewhere.has(toId(s)));
  };

  const addRoom = () => {
    setAllocations((prev) => [
      ...prev,
      {
        roomId: "",
        invigilatorIds: [],
        seatAllocations: [],
        startTime: schedule?.startTime || "",
        endTime: schedule?.endTime || "",
        totalBenches: 0,
        seatsPerBench: 1,
        capacity: 0,
      },
    ]);
  };

  const updateAllocation = (index, next) => {
    setAllocations((prev) => prev.map((a, i) => (i === index ? next : a)));
  };

  const removeAllocation = (index) => {
    setAllocations((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (allocations.length === 0) {
      toast.error("Add at least one room");
      return;
    }
    if (allocations.some((a) => !a.roomId)) {
      toast.error("Select a room for each allocation");
      return;
    }

    for (const a of allocations) {
      for (const s of a.seatAllocations || []) {
        if (!s.studentId) continue;
        if (!s.sectionId) {
          const st = students.find((x) => toId(x) === toId(s.studentId));
          if (st) s.sectionId = toId(st.sectionId);
        }
        if (!s.sectionId) {
          toast.error(
            "Each seated student must have a section. Filter/load students and re-seat."
          );
          return;
        }
      }
    }
    try {
      setSubmitting(true);
      const classId = toId(schedule?.classId);
      const payload = {
        examScheduleId: scheduleId,
        rooms: allocations.map((a) => ({
          roomId: a.roomId,
          invigilatorIds: a.invigilatorIds,
          startTime: a.startTime || "",
          endTime: a.endTime || "",
          seatAllocations: (a.seatAllocations || []).map((s) => ({
            benchNo: Number(s.benchNo),
            seatIndex: Number(s.seatIndex) || 1,
            studentId: toId(s.studentId),
            classId: toId(s.classId) || classId,
            sectionId: toId(s.sectionId),
            seatNumber: s.seatNumber || "",
          })),
        })),
        notes,
      };
      if (plan?._id) {
        if (isPortal)
          await updateSeatingPlanPortal(toId(plan), {
            rooms: payload.rooms,
            notes,
          });
        else
          await updateSeatingPlan(toId(plan), {
            rooms: payload.rooms,
            notes,
          });
        toast.success("Seating plan updated");
      } else {
        if (isPortal) await createSeatingPlanPortal(payload);
        else await createSeatingPlan(payload);
        toast.success("Seating plan created");
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatus = async (status) => {
    if (!plan?._id) return;
    try {
      setSubmitting(true);
      if (isPortal) await updateSeatingPlanStatusPortal(toId(plan), status);
      else await updateSeatingPlanStatus(toId(plan), status);
      toast.success(`Plan marked ${status}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Status update failed");
    } finally {
      setSubmitting(false);
    }
  };

  const sectionOptions = useMemo(
    () => [
      { label: "All sections in class", value: "" },
      ...sections.map((s) => ({
        label: s.sectionName || s.name,
        value: toId(s),
      })),
    ],
    [sections]
  );

  if (loading) {
    return (
      <div className="mx-auto max-w-6xl space-y-6 p-6">
        <Skeleton height={24} width="150px" />
        <Skeleton height={100} width="100%" />
        <Skeleton height={300} width="100%" />
      </div>
    );
  }

  const examId = toId(schedule?.examId) || toId(schedule?.examId?._id);
  const classId = toId(schedule?.classId);

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      {/* Navigation Breadcrumbs */}
      <div className="flex items-center gap-4 text-sm">
        <button
          type="button"
          onClick={() =>
            navigate(examId ? `${examsPath}/${examId}` : examsPath)
          }
          className="inline-flex items-center gap-1 font-medium text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Back to exam
        </button>
        <button
          type="button"
          onClick={() => navigate(schedulesPath)}
          className="font-medium text-primary-900 dark:text-primary-300 hover:underline"
        >
          All schedules
        </button>
      </div>

      {/* Header Info Card */}
      <Card>
        <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
              <MapPin className="h-6 w-6 text-primary-900 dark:text-primary-300 shrink-0" />
              Seating Plan
            </CardTitle>
            {schedule && (
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
                {schedule.subjectId?.name || "Subject"} ·{" "}
                {schedule.classId?.className || "Class"} ·{" "}
                {formatDate(schedule.examDate)} · Max {schedule.maxMarks}
              </p>
            )}
          </div>
          {plan && (
            <div>
              <Badge variant={plan.status === "FINAL" ? "success" : "warning"}>
                {plan.status}
              </Badge>
            </div>
          )}
        </CardHeader>
      </Card>

      {/* Alerts */}
      
      

      {/* Section Filter Card */}
      <Card>
        <CardContent className="p-4 space-y-2">
          <div className="w-full sm:w-64">
            <Select
              label="Filter students by section"
              options={sectionOptions}
              value={sectionFilter}
              onChange={(val) => handleSectionFilter(val)}
            />
          </div>
          <p className="text-sm text-neutral-400 dark:text-neutral-500 mt-0.5">
            {students.length} enrolled student(s) available to seat
          </p>
        </CardContent>
      </Card>

      {/* Room Allocations List */}
      <div className="space-y-4">
        {allocations.map((a, i) => (
          <SeatingRoomCard
            key={i}
            index={i}
            allocation={a}
            roomOptions={rooms}
            studentOptions={studentsForRoom(i)}
            classId={classId}
            isPortal={isPortal}
            onChange={updateAllocation}
            onRemove={removeAllocation}
            disabled={!canManage || locked}
          />
        ))}
      </div>

      {canManage && !locked && (
        <Button
          type="button"
          variant="outline-primary"
          onClick={addRoom}
          leftIcon={<Plus className="h-4 w-4" />}
        >
          Add Room
        </Button>
      )}

      {/* Notes Section Card */}
      <Card>
        <CardContent className="p-4">
          <Textarea
            label="Notes"
            rows={2}
            disabled={!canManage || locked}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any seating instructions or notes here..."
          />
        </CardContent>
      </Card>

      {/* Action Buttons */}
      {canManage && (
        <div className="flex flex-wrap gap-3">
          {!locked && (
            <Button
              type="button"
              variant="primary"
              isLoading={submitting}
              onClick={handleSave}
            >
              {plan ? "Save Plan" : "Create Plan"}
            </Button>
          )}
          {plan && plan.status === "DRAFT" && (
            <Button
              type="button"
              variant="accent"
              isLoading={submitting}
              onClick={() => handleStatus("FINAL")}
            >
              Finalize
            </Button>
          )}
          {plan && plan.status === "FINAL" && (
            <Button
              type="button"
              variant="secondary"
              isLoading={submitting}
              onClick={() => handleStatus("DRAFT")}
            >
              Reopen to DRAFT
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ExamSeatingPage;